import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { COUNT_STATUSES } from "../util/inventoryCount";
import { every, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const INVENTORY_COUNT_FIELDS = {
  title: "",
  status: "active", // "creating" (lines being written) | "active" | "complete"
  scope: {
    warehouses: [],
    includeVehicles: false,
  },
  // The frozen expected snapshot and counting progress live one document per
  // line in inventoryCounts/{id}/lines (a whole warehouse is past the 1 MiB
  // one document can hold). See src/util/inventoryCount.js for the line shape
  // and reconciliation math. The count keeps what its list shows:
  lineCount: 0, // expected (non-extra) lines
  countedCount: 0, // expected lines counted so far
  reconciliation: null, // summarizeCount() of the lines, frozen at Complete
  notes: "",

  // Meta info
  created: null,
  createdBy: null,
  completed: null,
  completedBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(INVENTORY_COUNT_FIELDS);

export default class InventoryCount extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, INVENTORY_COUNT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.completed = timestampToDate(data.completed);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: InventoryCount.canBeViewedBy,
      create: InventoryCount.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isAdmin || user.hasRole("warehouse");
  }

  canBeEditedBy(user) {
    return (
      this.data.status === "active" &&
      (user.isAdmin || user.hasRole("warehouse"))
    );
  }

  canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return InventoryCount.getPath(this.id);
  }

  static getPath(id) {
    let path = `inventoryCounts`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static getLinePath(id, lineID) {
    let path = `${InventoryCount.getPath(id)}/lines`;
    if (lineID) {
      path += `/${lineID}`;
    }
    return path;
  }

  static toFirestore(count) {
    return count.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new InventoryCount(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      title: [(a) => Boolean(a && a.trim()) || "required"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(
      !this.id && isEmpty(diff(this.data, INVENTORY_COUNT_FIELDS))
    );
  }

  isEqual(count) {
    return Boolean(
      this.id === count.id && isEmpty(diff(this.data, count.data))
    );
  }

  // Formatting

  get isComplete() {
    return this.data.status === "complete";
  }

  /**
   * The figures the list shows, without the lines: progress from the
   * counters while counting, then the reconciliation frozen at Complete. The
   * count page summarizes its loaded lines itself (summarizeCount).
   */
  get summary() {
    if (this.isComplete && this.data.reconciliation) {
      return this.data.reconciliation;
    }
    const expectedLines = this.data.lineCount;
    const countedLines = this.data.countedCount;
    return {
      expectedLines,
      countedLines,
      progress: expectedLines ? countedLines / expectedLines : 0,
      discrepancies: null,
      costVariance: null,
    };
  }

  get status() {
    const type = COUNT_STATUSES[this.data.status];
    return type
      ? { ...type, ...COLORS[type.color], type: this.data.status }
      : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get completedBy() {
    return this.data.completedBy
      ? store.state.main.users[this.data.completedBy] || null
      : null;
  }

  toString() {
    return this.data.title;
  }
}
